/**
 * CompareSubTab — 원본 vs HAI 비교 (탭3-c).
 *
 * 본 스펙: AIFYAGENT/MONITORING_SPEC.md §6.c §10 §11 Phase 5
 *
 * 입력: tenantCode + bsda + promptId
 * 동작: api-gateway 의 /api/tenants/{id}/prompts/{prompt}/compare 호출 후
 *       (source_id, ingestion) 페어 별로 결정적 metric 표시.
 *
 * prompt-agnostic — 응답 스키마는 prompt 와 무관하게 동일하며, OPERATION_RATE
 * 계열 (header == [가동일,담당자,가동률]) 에만 의미 있는 항목은 별도 박스로 표시.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";

import {
  bucketDownloadUrl,
  fetchCompare,
  type CompareChecks,
  type ComparePair,
} from "../api/monitoring";

interface CompareSubTabProps {
  tenantCode: string;
  bsda: string;
  promptId: string;
}

const CHECK_LABEL: Record<keyof CompareChecks, string> = {
  header_exact: "헤더 일치",
  no_duplicates: "중복 없음",
  no_zero_rate: "0% 없음",
  no_dates_after_cutoff: "추출일 초과 없음",
  rows_gte_5: "5행 이상",
};

const EXPECTED_HEADER = ["가동일", "담당자", "가동률"];

function hasOperationRateShape(header: string[]): boolean {
  if (header.length !== 3) return false;
  for (let i = 0; i < EXPECTED_HEADER.length; i++) {
    if (header[i] !== EXPECTED_HEADER[i]) return false;
  }
  return true;
}

function CheckChip({ ok, label }: { ok: boolean; label: string }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      color={ok ? "success" : "error"}
      label={`${ok ? "✓" : "✗"} ${label}`}
    />
  );
}

function rowDiff(pair: ComparePair): { isDiff: boolean; reason: string | null } {
  // 차이 행 highlight 조건 — overall_pass 가 false 거나 source 합 vs HAI rows 가 큰 폭으로 차이.
  if (!pair.overall_pass) {
    return { isDiff: true, reason: "체크 실패 항목 있음" };
  }
  // 원본 대비 HAI 행수 (성능 측면은 일치 보장 아님, 보조 지표).
  if (
    pair.hai.rows > 0 &&
    pair.source.total_data_rows > 0 &&
    Math.abs(pair.source.total_data_rows - pair.hai.rows) /
      Math.max(pair.source.total_data_rows, pair.hai.rows) >=
      0.5
  ) {
    return { isDiff: true, reason: "행수 차이 50% 이상" };
  }
  return { isDiff: false, reason: null };
}

export function CompareSubTab({ tenantCode, bsda, promptId }: CompareSubTabProps) {
  const [pairs, setPairs] = useState<ComparePair[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cached, setCached] = useState<boolean>(false);

  const load = useCallback(async () => {
    if (!tenantCode || !promptId || !/^\d{8}$/.test(bsda)) {
      setPairs(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchCompare(tenantCode, promptId, bsda);
      setPairs(resp.pairs);
      setCached(resp.cached);
    } catch (e) {
      setPairs(null);
      setError(e instanceof Error ? e.message : "비교 결과 조회 실패");
    } finally {
      setLoading(false);
    }
  }, [tenantCode, promptId, bsda]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle1" fontWeight={600}>
          원본 ↔ HAI 비교
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          sx={{ fontFamily: "monospace" }}
          label={`${promptId} · ${bsda}`}
        />
        {cached && <Chip size="small" label="cached" variant="outlined" />}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon fontSize="small" />}
          onClick={load}
          disabled={loading}
        >
          새로고침
        </Button>
      </Stack>

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            비교 metric 계산 중… (원본/HAI 다운로드 + xlsx 분석)
          </Typography>
        </Stack>
      )}

      {error && <Alert severity="warning">{error}</Alert>}

      {!loading && !error && pairs && pairs.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            (테넌트, prompt, BSDA) 에 대응되는 (원본, HAI) 페어가 없습니다.
            HAI 변환이 완료되지 않았거나 prompt 이름이 다를 수 있습니다.
          </Typography>
        </Paper>
      )}

      {pairs && pairs.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>채널 / 좌표</TableCell>
                <Tooltip title="원본 xlsx 의 sheet 전체 데이터 행 합 (헤더 1행 제외)" arrow>
                  <TableCell align="right">원본 행수</TableCell>
                </Tooltip>
                <Tooltip title="HAI xlsx 의 데이터 행수" arrow>
                  <TableCell align="right">HAI 행수</TableCell>
                </Tooltip>
                <TableCell align="right">고유 날짜</TableCell>
                <TableCell align="right">고유 인원</TableCell>
                <Tooltip title="OPERATION_RATE 계열 prompt 에서만 의미 있음 — 가동률 합" arrow>
                  <TableCell align="right">RATE_SUM</TableCell>
                </Tooltip>
                <TableCell>날짜 누락</TableCell>
                <TableCell>체크</TableCell>
                <TableCell>다운로드</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pairs.map((p, idx) => {
                const diff = rowDiff(p);
                const opShape = hasOperationRateShape(p.hai.header);
                return (
                  <TableRow
                    key={`${p.channel}-${p.source_id}-${p.ingestion_id}-${idx}`}
                    sx={{
                      bgcolor: diff.isDiff ? "error.50" : undefined,
                    }}
                  >
                    <TableCell>
                      <Stack spacing={0.2}>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: "monospace", fontWeight: 600 }}
                        >
                          {p.channel} / {p.source_id}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: "monospace", color: "text.secondary" }}
                        >
                          {p.ingestion_id}
                        </Typography>
                        {diff.reason && (
                          <Typography variant="caption" color="error.main">
                            ⚠ {diff.reason}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={p.source.sheets
                          .map((s) => `${s.name}: ${s.rows} × ${s.cols}`)
                          .join(" / ")}
                        arrow
                      >
                        <Typography variant="caption">
                          {p.source.total_data_rows}
                          {p.source.sheet_count > 1 && (
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 0.5 }}
                            >
                              ({p.source.sheet_count} sheets)
                            </Typography>
                          )}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="caption"
                        color={
                          p.source.total_data_rows > 0 &&
                          p.hai.rows !== p.source.total_data_rows
                            ? "warning.main"
                            : undefined
                        }
                        fontWeight={
                          p.source.total_data_rows > 0 &&
                          p.hai.rows !== p.source.total_data_rows
                            ? 600
                            : 400
                        }
                      >
                        {p.hai.rows}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption">
                        {p.hai.unique_date_count}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption">
                        {p.hai.unique_person_count}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {opShape ? (
                        <Tooltip
                          title={`min=${p.hai.rate_min ?? "—"} / max=${p.hai.rate_max ?? "—"} / zero_count=${p.hai.rate_zero_count}`}
                          arrow
                        >
                          <Typography
                            variant="caption"
                            color={
                              p.hai.rate_zero_count > 0 ? "error.main" : undefined
                            }
                            fontWeight={p.hai.rate_zero_count > 0 ? 600 : 400}
                          >
                            {p.hai.rate_sum}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {p.hai.missing_dates_in_month.length === 0 ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="success"
                          label="없음"
                        />
                      ) : (
                        <Tooltip
                          title={p.hai.missing_dates_in_month.join(", ")}
                          arrow
                        >
                          <Chip
                            size="small"
                            variant="outlined"
                            color="error"
                            label={`${p.hai.missing_dates_in_month.length}일 누락`}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {(
                          Object.keys(CHECK_LABEL) as Array<keyof CompareChecks>
                        ).map((k) => (
                          <CheckChip
                            key={k}
                            ok={p.checks[k]}
                            label={CHECK_LABEL[k]}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title={p.source_gcs_path} arrow>
                          <Link
                            href={bucketDownloadUrl(tenantCode, p.source_gcs_path)}
                            target="_blank"
                            rel="noopener"
                            underline="hover"
                            sx={{
                              fontSize: 12,
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            <DownloadIcon fontSize="inherit" sx={{ mr: 0.3 }} />
                            원본
                          </Link>
                        </Tooltip>
                        <Tooltip title={p.hai_gcs_path} arrow>
                          <Link
                            href={bucketDownloadUrl(tenantCode, p.hai_gcs_path)}
                            target="_blank"
                            rel="noopener"
                            underline="hover"
                            sx={{
                              fontSize: 12,
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            <DownloadIcon fontSize="inherit" sx={{ mr: 0.3 }} />
                            HAI
                          </Link>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}
